<script setup lang="ts">
import Table from "@/components/Reusables/Table/Table.vue";
import type { LevelsTableParentGroup } from "./LevelsLifetimesTypes";

defineProps<{
  items: LevelsTableParentGroup[];
}>();

const emits = defineEmits<{
  (e: "select", lifetime: number): void;
}>();

const handleSelectLifetime = (lifetime: number | null) => {
  if (!lifetime) return;
  emits("select", lifetime);
};
</script>

<template>
  <Table
    class="h-full"
    caption="Level(s) service lifetimes"
    table-class="table-fixed border-separate border-spacing-0 border border-slate-200">
    <template #thead>
      <thead class="text-type-muted sticky top-0 h-[40px] border-b border-slate-400 bg-slate-200">
        <tr class="border-b">
          <td class="t-small-bold w-2/3 bg-slate-200 px-4 py-3 text-left tracking-wide text-slate-700">Material / Product</td>
          <td class="t-small-bold max-w-[18rem] bg-slate-200 px-3 py-3 text-center tracking-wide text-slate-700">
            Lifetime (years)
          </td>
        </tr>
      </thead>
    </template>

    <template #tbody>
      <tbody
        v-for="parent in items"
        :key="parent.id">
        <!-- Parent section row -->
        <tr class="border-b border-slate-300">
          <td
            class="z-sticky sticky top-[40px] h-[40px] bg-slate-200 px-4 py-2"
            colspan="2">
            <div class="t-small-bold flex items-baseline gap-2 tracking-wide">
              <span>{{ parent.label }}</span>
            </div>
          </td>
        </tr>

        <template
          v-for="child in parent.children"
          :key="child.id">
          <!-- Building part header row -->
          <tr class="border-b border-slate-200 bg-slate-100">
            <td
              class="z-sticky sticky top-[79px] border-t border-slate-200 bg-slate-100 px-4 py-2"
              colspan="2">
              <div class="flex items-center gap-2 px-1 py-1">
                <span class="bg-app-blue-200 rounded-4 t-caption-bold inline-flex items-center px-2 py-0.5 text-slate-700">
                  {{ child.label }}
                </span>
              </div>
            </td>
          </tr>

          <!-- Element rows -->
          <tr
            v-for="row in child.rows"
            :key="row.id"
            class="border-b border-slate-200">
            <td class="border-b border-slate-200 px-4 py-2">
              <div class="flex min-w-0 items-center gap-2">
                <div class="min-w-0 border-slate-200 pl-3">
                  <span class="t-xs block truncate">{{ row.elementLabel }}</span>
                </div>
              </div>
            </td>

            <td class="max-w-[18rem] overflow-hidden border-b border-slate-200 px-3 py-2">
              <div class="grid w-full grid-cols-3 gap-1">
                <template v-if="row.avg != null && row.low == null && row.high == null">
                  <button
                    v-tooltip="{ text: `Select average lifetime (${row.avg} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 focus-ring bg-slate-0 duration-fast ease-standard col-span-3 flex flex-col items-center border border-slate-300 px-3 py-1 shadow-xs transition-colors"
                    :aria-label="`Select average lifetime ${row.avg} years for ${row.elementLabel}`"
                    @click="handleSelectLifetime(row.avg)">
                    <span class="t-footnote text-type-muted tracking-wide uppercase">Avg</span>
                    <span class="t-small-bold text-type-default">{{ row.avg }}</span>
                  </button>
                </template>
                <template v-else>
                  <button
                    v-if="row.low != null"
                    v-tooltip="{ text: `Select low lifetime (${row.low} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 focus-ring bg-slate-0 duration-fast ease-standard flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    :aria-label="`Select low lifetime ${row.low} years for ${row.elementLabel}`"
                    @click="handleSelectLifetime(row.low)">
                    <span class="t-footnote text-type-muted tracking-wide uppercase">↓ Low</span>
                    <span class="t-small-bold text-type-default">{{ row.low }}</span>
                  </button>
                  <button
                    v-if="row.avg != null"
                    v-tooltip="{ text: `Select average lifetime (${row.avg} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 focus-ring bg-slate-0 duration-fast ease-standard flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    :aria-label="`Select average lifetime ${row.avg} years for ${row.elementLabel}`"
                    @click="handleSelectLifetime(row.avg)">
                    <span class="t-footnote text-type-muted tracking-wide uppercase">Avg</span>
                    <span class="t-small-bold text-type-default">{{ row.avg }}</span>
                  </button>
                  <button
                    v-if="row.high != null"
                    v-tooltip="{ text: `Select high lifetime (${row.high} years)`, variant: 'label', placement: 'top' }"
                    type="button"
                    class="hover:border-app-blue-500 hover:bg-app-blue-50 rounded-4 focus-ring bg-slate-0 duration-fast ease-standard flex flex-col items-center border border-slate-300 px-2 py-1 shadow-xs transition-colors"
                    :aria-label="`Select high lifetime ${row.high} years for ${row.elementLabel}`"
                    @click="handleSelectLifetime(row.high)">
                    <span class="t-footnote text-type-muted tracking-wide uppercase">↑ High</span>
                    <span class="t-small-bold text-type-default">{{ row.high }}</span>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </template>
  </Table>
</template>
